<template>
  <div class="accountsigninup">
    <div class="accountsigninup__wrapper--centered">
      <div class="accountsigninup__selector">
        <div class="accountsigninup__selector-wrapper">
          <button
            class="btn btn--responsive"
            :class="currentForm === 'register' ? 'btn--primary' : 'btn--secondary'"
            @click="currentForm = 'register'"
          >
            Sign up
          </button>
          <button
            class="btn btn--responsive"
            :class="currentForm === 'login' ? 'btn--primary' : 'btn--secondary'"
            @click="currentForm = 'login'"
          >
            Sign in
          </button>
        </div>
      </div>
      <transition-group name="fade" tag="div" class="accountsigninup__forms">
        <div v-if="currentForm === 'login'" class="" key="login">
          <UserLoginForm key="login" v-if="!isMFAFormShown" redirectUrl="payment" />
          <TwoFactorForm key="two-factor" v-else />
        </div>
        <div
          v-if="currentForm === 'register'"
          class="accountsigninup__forms-register"
          key="register"
        >
          <UserRegistrationForm
            v-if="!isComponentLoading"
            :isLoading="isLoadingCaptcha"
            :isAuthenticated="isAuthenticated"
            :profile="{}"
            @submit-form="fetchRegisterRecaptcha"
            key="register"
            class="accountsigninup__form"
          />
          <vue-loaders v-else name="line-scale" color="#ef7d22"></vue-loaders>
        </div>
      </transition-group>
      <CaptchaBlock
        ref="invisibleRecaptcha"
        keyType="login"
        @captcha-verify-action="submitRegisterForm"
      />
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import UserRegistrationForm from '@/components/login/registration/UserRegistrationForm.vue'
import CaptchaBlock from '@/components/shared/CaptchaBlock.vue'
import UserLoginForm from '@/components/login/UserLoginForm.vue'
import TwoFactorForm from '@/components/login/two-factor/TwoFactorForm.vue'
import { eventsConstants } from '@/helpers/eventsList'
import { mixpanelEvent } from '@/helpers/mixins'
import { trackWoopraEvent } from '@/helpers'

export default {
  name: 'AccountSignInUp',
  components: {
    UserLoginForm,
    UserRegistrationForm,
    TwoFactorForm,
    CaptchaBlock,
  },
  mixins: [mixpanelEvent],
  data() {
    return {
      email: '',
      password: '',
      currentForm: 'register',
      registrationFields: null,
      error: {
        type: null,
        message: null,
      },
    }
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
      isLoadingCaptcha: state => state.app.isLoadingCaptcha,
      isMFAFormShown: state => state.app.isMFAFormShown,
      isComponentLoading: state => state.app.isComponentLoading,
      userLeadAlias: state => state.auth.userLeadAlias,
      utmParams: state => state.events.utmParams,
    }),
  },
  methods: {
    fetchRegisterRecaptcha(registrationFields) {
      this.$store.dispatch('app/setIsComponentLoading', true)
      this.registrationFields = registrationFields
      this.$refs.invisibleRecaptcha.execute()
    },
    submitCodeHandler(value) {
      this.isSubmittingMFA = value
    },
    async submitRegisterForm() {
      try {
        this.$store.dispatch('app/setIsComponentLoading', true)
        const { email, first_name, last_name, password } = this.registrationFields
        const { recaptcha } = this
        this.$store.dispatch('auth/createUserAccount', {
          email,
          first_name,
          last_name,
          password,
          recaptcha,
        })
        this.$store.dispatch('subscriptions/updatePaymentEntries', {
          value: 'first_name',
          input: first_name,
        })
        this.$store.dispatch('subscriptions/updatePaymentEntries', {
          value: 'last_name',
          input: last_name,
        })
        await this.$store.dispatch('auth/createLeadAlias', email)
        this.sendMixpanelEvent(eventsConstants.CHECKOUT_CART_UPDATED, {
          trigger: 'createUserAccount',
        })
        const GAObj = {
          event: eventsConstants.CHECKOUT_GA_SIGN_UP,
          uaa_lead: this.userLeadAlias,
        }
        await this.$store.dispatch('events/sendGAEvent', GAObj)

        const userData = {
          email,
          firstname: first_name,
          lastname: last_name,
        }

        trackWoopraEvent('sign up', userData, this.utmParams)

        this.$router.push({ name: 'payment' })
        this.$store.dispatch('app/setIsComponentLoading', false)
      } catch (error) {
        this.onCaptchaExpire()
      } finally {
        this.$store.dispatch('app/setIsComponentLoading', false)
      }
    },
  },
  async created() {
    try {
      if (this.isAuthenticated) {
        this.$router.push({ name: 'payment' })
      }
    } catch (error) {
      this.$store.dispatch('app/setIsComponentLoading', false)
    }
    if (this.$route.params.action === 'login' || this.isMFAFormShown) this.currentForm = 'login'
    this.$store.dispatch('app/setIsComponentLoading', false)
  },
}
</script>
<style lang="scss" scoped>
.accountsigninup {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  position: relative;
  &__wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 110px 60px;
    &--centered {
      width: 100%;
    }
    @include tablet {
      border-radius: 35px 0px 0px 35px;
      padding: 60px;
    }
  }
  &__selector {
    display: flex;
    justify-content: center;
    &-wrapper {
      background: linear-gradient(
        112.2deg,
        rgba(187, 188, 199, 0.4) -46.39%,
        rgba(187, 188, 199, 0) 112.38%
      );
      border-radius: 8px;
      width: 100%;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      @include mobile {
        width: 370px;
      }
    }
    :global(.btn--secondary) {
      border: none;
    }
    @include tablet {
      :global(.btn) {
        width: 180px;
      }
    }
  }
  &__forms {
    margin: 50px auto;
    margin-bottom: 0;
    position: relative;
    max-width: 600px;
    @include tablet {
      margin: 50px 0 0 0;
    }
    &-register {
      display: flex;
      justify-content: center;
    }
  }
  .fade-leave-active {
    position: absolute;
    top: 0;
    width: 100%;
  }
}
</style>
